/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Route, Routes } from 'react-router-dom';
import FormWrapper from './pages/FormWrapper';
import { NotFound } from './pages/NotFound';
import NavBar from './components/Navbar';
import SideMenu from './components/sideMenu';
import { useKeycloak } from '@react-keycloak/web';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Alert } from './components/Alert';

interface UserInfoWithGroups {
  groups?: string[];
  email?: string;
  /** Keycloak subject (stable user id). */
  sub?: string;
  [key: string]: unknown;
}

// sessionStorage flag: we already forced a logout to refresh a missing clinic
// group. Survives the logout→login redirect within the tab, and prevents an
// infinite logout↔login loop when the account genuinely has no group in
// Keycloak.
const MISSING_REF_LOGOUT_FLAG = 'momcare.missingRefLogout';

function App() {
  const { keycloak, initialized } = useKeycloak();
  const [groups, setGroups] = useState<string[]>([]);
  // Submitter identity — carried into the submission (hidden fields) so async
  // errors can be routed back to the right user. Not gated on; submission still
  // works if Keycloak omits these.
  const [submitter, setSubmitter] = useState<{ email?: string; id?: string }>({});
  // Distinguishes "userinfo still loading" from "loaded and has no groups" —
  // without this we'd wrongly log users out during the brief loading window.
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);

  useEffect(() => {
    if (initialized && keycloak.authenticated) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      keycloak
        .loadUserInfo()
        .then((info: UserInfoWithGroups) => {
          if (Array.isArray(info.groups)) {
            setGroups(info.groups);
          }
          setSubmitter({
            email: typeof info.email === 'string' ? info.email : undefined,
            id: typeof info.sub === 'string' ? info.sub : undefined,
          });
        })
        .catch(() => {
          // Treated as "no groups" — handled by the guard below.
        })
        .finally(() => {
          setUserInfoLoaded(true);
        });
    }
  }, [initialized, keycloak]);

  const userGroup = groups[0] || '';

  // Fresh Keycloak bearer token for the mediator calls (validate/status). Refreshes
  // if near expiry; falls back to the current token if refresh fails.
  const getToken = useCallback(async (): Promise<string | undefined> => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      await keycloak.updateToken(30);
    } catch {
      // refresh failed — use whatever token we have (may be stale/undefined)
    }
    return keycloak.token;
  }, [keycloak]);

  // Once userinfo has resolved, enforce that a clinic/facility reference is
  // present. If it isn't, force a fresh login so a new token — which may now
  // carry the group — is picked up from Keycloak. Guarded so we only retry
  // once and don't loop when the account truly has no group.
  useEffect(() => {
    if (!userInfoLoaded) return;

    if (userGroup) {
      sessionStorage.removeItem(MISSING_REF_LOGOUT_FLAG);
      return;
    }

    if (sessionStorage.getItem(MISSING_REF_LOGOUT_FLAG)) {
      // Already retried after a fresh login and STILL no group — stop looping.
      return;
    }

    sessionStorage.setItem(MISSING_REF_LOGOUT_FLAG, '1');
    void keycloak.logout({ redirectUri: window.location.origin });
  }, [userInfoLoaded, userGroup, keycloak]);

  // Runtime config — set in public/env-config.js (dev) or by docker-entrypoint.sh (prod)
  const cfg = window.__APP_CONFIG__;
  const splitGroups = (v?: string): string[] =>
    (v ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const matchesGroup = (groups: string[]): boolean =>
    groups.some((g) => userGroup.toLowerCase().includes(g.toLowerCase()));
  // A test clinic loads the dedicated test form and (when env=test) is the only
  // form that talks to the mediator. Test takes priority over the social check —
  // but ONLY when a test form is actually configured, so a TEST_GROUPS entry
  // without FORM_KEY_TEST falls back to the normal form instead of loading "".
  const isTest = Boolean(cfg.formKeyTest) && matchesGroup(splitGroups(cfg.testGroups));
  const isSocial = !isTest && matchesGroup(splitGroups(cfg.socialGroups));
  const formkey = isTest
    ? (cfg.formKeyTest as string)
    : isSocial
      ? cfg.formKeySocial
      : cfg.formKeyDefault;

  // Gate rendering on a resolved, non-empty clinic reference so no form can be
  // filled (let alone submitted) without one.
  let content: ReactNode;
  if (!userInfoLoaded) {
    content = (
      <div className="flex items-center justify-center" style={{ height: '50vh' }}>
        <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-primary animate-spin" />
      </div>
    );
  } else if (!userGroup) {
    content = (
      <Alert variant="error">
        No clinic/facility is assigned to your account. Signing you out to
        refresh your access — if this keeps happening, please contact support.
      </Alert>
    );
  } else {
    content = (
      <FormWrapper
        formKey={formkey}
        userType="Mom"
        userGroup={userGroup}
        submitterEmail={submitter.email}
        submitterId={submitter.id}
        getToken={getToken}
      />
    );
  }

  return (
    <>
      <SideMenu />
      <NavBar />
      <div
        className="App p-3"
        style={{ marginLeft: '250px', paddingTop: '56px' }}
      >
        <Routes>
          <Route path="/">
            <Route index={true} element={content} />
            <Route path="*" element={<NotFound />} />
            {/* <Route
              path="/enroll_baby"
              element={<FormWrapper formKey="NDNWy7JL" userType="Baby" />}
            /> */}
          </Route>
        </Routes>
      </div>
    </>
  );
}

export default App;
